import type { ComponentProps } from "react";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import { usePlanPdfDrop } from "./usePlanPdfDrop";

// The "what changed" panel — the story of the amendment, above the sheet table.
//
// Why it exists: nobody can verify thirty change notes by reading thirty table
// rows, so they get rubber-stamped. What a person CAN take in before scanning is
// the gist — what this filing actually does — and the tally that says how much of
// it is finished.
//
// WHAT THIS REPLACED, AND WHY. Until now the panel listed the amendment's
// REASONS: three to six phrases, each with a receipt (the sheet it was read
// from, the before/after showing it, how many notes cite it), editable and
// removable. That had a real job — the form was about to assert "revised zoning
// analysis PER REVISED BUILDING BULK", and a cause is the one thing on an AI1
// nobody can check against a drawing, so it deserved review before signing.
//
// Two things ended it. The sheet rows now carry their own note AND their own
// plain-English evidence, so the receipts duplicated what was already one line
// below. And the office can now switch the "per …" clause off entirely
// (AiParsing:WhatChangedCauseAttribution) — at which point the panel was
// reviewing a claim the form never makes. A window onto an internal
// intermediate.
//
// So: one paragraph, written by the same call that writes the notes, editable.
// Editing it is NOT cosmetic — it goes back into the next composition as the
// operator's own account of the filing, which is how you still correct a wrong
// story once instead of thirty times. Nothing on the form moves until Rewrite,
// and a rewrite costs one call because the server still holds the indexed sets.

export type CompareState =
  | { status: "idle" }
  | {
      status: "running";
      fileName: string;
      message: string;
      total: number;
      done: number;
      rowScoped: boolean;
    }
  | { status: "done"; fileName: string; noteCount: number; skippedCount: number }
  | { status: "error"; fileName: string; message: string };

/** What the last comparison concluded across the whole set. Counted from the
 *  rows, not reported by the engine, so it can never disagree with the table. */
export interface CompareTally {
  /** Sheets the router cleared — reissued, not revised. */
  unchanged: number;
  /** Sheets that changed and got a described note. */
  described: number;
  /** Sheets that need a person: changed but indescribable, or uncomparable. */
  needsReview: number;
  /** Sheets whose content changed but whose printed revision never moved. */
  staleRevision: number;
}

export function WhatChangedPanel({
  compare,
  tally,
  gist,
  designIntent,
  dirty,
  rewriting,
  rewritableCount,
  busy,
  approvedFileName,
  compareSheets,
  compareEstimateUsd,
  onCompare,
  onGistChange,
  onDesignIntentChange,
  onRewrite,
  onFile,
  onCancel,
}: {
  compare: CompareState;
  /** Set-wide outcome of the last comparison; absent before one has run. */
  tally?: CompareTally | null;
  /** The amendment in a paragraph, from the notes call. Empty before a run. */
  gist: string;
  designIntent: string;
  /** The gist or the intent edited since the notes were last written. */
  dirty: boolean;
  rewriting: boolean;
  /** How many notes a rewrite would touch (compared sheets, minus hand-typed). */
  rewritableCount: number;
  busy: boolean;
  /** The approved set already in hand, if any. Having it is NOT the same as
   *  having asked for anything to be done with it — see the idle state below. */
  approvedFileName?: string | null;
  /** Sheets a comparison would send to the AI, and what that is worth. Shown
   *  BEFORE the run rather than in a dialog after it: the operator deciding
   *  whether to spend is the operator reading this line. */
  compareSheets?: number;
  compareEstimateUsd?: string;
  onCompare?: () => void;
  onGistChange: (value: string) => void;
  onDesignIntentChange: (value: string) => void;
  onRewrite: () => void;
  onFile: (file: File) => void;
  onCancel: () => void;
}) {
  const compared = compare.status === "done" || gist.trim().length > 0;
  const controlsDisabled = busy || rewriting || compare.status === "running";
  // The whole panel takes a dropped approved set, not just its button — the same
  // gesture that brought the updated plan in on the upload page.
  const approved = usePlanPdfDrop(onFile, busy || compare.status === "running");

  return (
    <section
      {...approved.dropProps}
      className={cn(
        "mt-6 overflow-hidden rounded-lg border bg-surface shadow-soft transition-colors",
        approved.dragging ? "border-dashed border-primary bg-primary-soft/40" : "border-rule",
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-6 py-3.5">
        <div className="text-[0.95rem] font-semibold text-ink">What changed</div>
        <StatusLine
          compare={compare}
          busy={busy}
          inputProps={approved.inputProps}
          onCancel={onCancel}
        />
      </header>

      {compare.status === "idle" && !compared ? (
        <div className="px-6 py-4">
          {approved.dragging ? (
            <p className="text-[0.88rem] leading-relaxed text-ink-soft">
              Drop the approved PDF here.
            </p>
          ) : approvedFileName ? (
            /* THE FILE IS HERE AND NOTHING HAS RUN.
               Uploading the approved set used to start this comparison by
               itself, which was wrong once it had a second use: an operator who
               brought the file in to combine the plan sets was billed for an AI
               pass over every sheet, and waited minutes for it, without ever
               asking. Having a file and spending money on it are now two
               separate decisions, and this is where the second one is made. */
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[0.9rem] font-medium text-ink">
                  Approved set ready —{" "}
                  <span className="font-normal text-ink-soft">{approvedFileName}</span>
                </div>
                <p className="mt-1 max-w-xl text-[0.86rem] leading-relaxed text-ink-soft">
                  Nothing has been sent yet. Describing what changed reads both
                  drawings of every revised sheet
                  {compareSheets && compareSheets > 0 ? (
                    <>
                      {" "}— {compareSheets} sheet{compareSheets === 1 ? "" : "s"}, about{" "}
                      <span className="font-medium text-ink">${compareEstimateUsd}</span>, a
                      few minutes
                    </>
                  ) : null}
                  . Combining the plan sets needs none of it and costs nothing.
                </p>
              </div>
              {onCompare && (
                <Button
                  intent="primary"
                  size="md"
                  onClick={onCompare}
                  disabled={busy || !compareSheets}
                  className="shrink-0"
                  title={
                    compareSheets
                      ? undefined
                      : "No superseding sheets to compare — add sheet numbers first"
                  }
                >
                  Describe what changed →
                </Button>
              )}
            </div>
          ) : (
            <p className="text-[0.88rem] leading-relaxed text-ink-soft">
              Drop the approved set here — or choose it above. It is what lets each
              superseding sheet get a change note, and what the combined plan set
              is built from. Nothing runs until you ask.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-5 px-6 py-5">
          <Tally tally={tally} />

          {/* The line that actually PRINTS, above the sheet list in Section 3.
              Two of the office's three filed AI1s write a specific one, and
              that is where they put the amendment's why — which is what lets
              the per-sheet lines stay purely descriptive. One line, because
              Section 3 gives it one. */}
          <div>
            <label
              htmlFor="paa-design-intent"
              className="label mb-1.5 block text-ink-muted"
            >
              Why is this amendment being filed?
            </label>
            <Textarea
              id="paa-design-intent"
              rows={2}
              value={designIntent}
              disabled={controlsDisabled}
              onChange={(e) => onDesignIntentChange(e.target.value)}
              placeholder="Optional — e.g. “minor as-built changes prior to DOB inspection” or “adding a 7th floor”"
              className="resize-none text-[0.88rem]"
            />
            <p className="mt-1.5 text-[0.78rem] leading-relaxed text-ink-muted">
              Prints as the opening line above the sheet list.
            </p>
          </div>

          {compared && (
            <div>
              <label htmlFor="paa-gist" className="label mb-1.5 block text-ink-muted">
                What this amendment does
              </label>
              <Textarea
                id="paa-gist"
                rows={4}
                value={gist}
                disabled={controlsDisabled}
                onChange={(e) => onGistChange(e.target.value)}
                placeholder="Written once the comparison finishes."
                className="resize-y text-[0.88rem] leading-relaxed"
              />
              <p className="mt-1.5 text-[0.78rem] leading-relaxed text-ink-muted">
                Not printed. Written from what the comparison found — correct it
                and rewrite, and the notes below follow your version.
              </p>
            </div>
          )}

          {compared && (
            <RewriteBar
              dirty={dirty}
              rewriting={rewriting}
              count={rewritableCount}
              disabled={busy || compare.status === "running"}
              onRewrite={onRewrite}
            />
          )}
        </div>
      )}
    </section>
  );
}

// What the comparison concluded across the set, in one line.
//
// This is the answer to the problem the whole feature exists for: nobody can
// verify forty change notes, so they get rubber-stamped. What CAN be verified is
// "thirty-one described, three need your eye" — a number small enough to act on,
// and the honest headline for a set nobody has time to read row by row. The
// review count leads because it is the only part that is not finished.
function Tally({ tally }: { tally?: CompareTally | null }) {
  if (!tally) return null;
  const { unchanged, described, needsReview, staleRevision } = tally;
  if (unchanged + described + needsReview === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[0.85rem] text-ink-soft">
      {needsReview > 0 ? (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-warn/40 bg-warn-soft px-2.5 py-0.5 font-medium text-ink">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-warn" />
          {needsReview} {needsReview === 1 ? "sheet needs" : "sheets need"} your eye
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-sage/40 bg-sage-soft px-2.5 py-0.5 font-medium text-ink">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-sage-ink" />
          every sheet accounted for
        </span>
      )}

      <span className="text-ink-muted">
        {described} described · {unchanged} unchanged
      </span>

      {staleRevision > 0 && (
        <span
          className="inline-flex items-center gap-1.5 text-ink-soft"
          title="These sheets changed but their title blocks still print the old revision. An examiner will catch it."
        >
          ·{" "}
          <span className="font-medium">
            {staleRevision} {staleRevision === 1 ? "revision" : "revisions"} not bumped
          </span>
        </span>
      )}
    </div>
  );
}

// The commit point. Nothing on the form changes until this runs, so rewriting
// the gist is always safe to explore.
function RewriteBar({
  dirty,
  rewriting,
  count,
  disabled,
  onRewrite,
}: {
  dirty: boolean;
  rewriting: boolean;
  count: number;
  disabled: boolean;
  onRewrite: () => void;
}) {
  if (rewriting) {
    return (
      <div className="flex items-center gap-2.5 border-t border-rule pt-4 text-[0.85rem] text-ink-soft">
        <span
          aria-hidden
          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-rule border-t-primary"
        />
        Rewriting the notes…
      </div>
    );
  }
  if (!dirty) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-4">
      <span className="text-[0.85rem] text-ink-soft">
        {count > 0
          ? `${count} note${count === 1 ? "" : "s"} were written before this edit.`
          : "No compared notes left to rewrite."}
      </span>
      <Button
        intent="soft"
        size="sm"
        onClick={onRewrite}
        disabled={disabled || count === 0}
      >
        Rewrite {count} note{count === 1 ? "" : "s"}
      </Button>
    </div>
  );
}

// Header right-hand side: the comparison's own state — invite, live progress,
// result, or failure — plus the file picker that (re)starts it.
function StatusLine({
  compare,
  busy,
  inputProps,
  onCancel,
}: {
  compare: CompareState;
  busy: boolean;
  /** The picker's input, from the panel's shared drop-or-browse hook. */
  inputProps: ComponentProps<"input">;
  onCancel: () => void;
}) {
  const pickLabel =
    compare.status === "idle"
      ? "Choose approved PDF"
      : compare.status === "error"
        ? "Try a different file"
        : "Compare a different set";

  const pick = (
    <label
      className={cn(
        "shrink-0 cursor-pointer rounded-lg border border-rule-strong bg-surface px-3 py-1.5 text-[0.82rem] font-medium text-ink hover:border-primary hover:bg-primary-soft/40",
        busy && "pointer-events-none opacity-60",
      )}
    >
      <input {...inputProps} />
      {pickLabel}
    </label>
  );

  if (compare.status === "running") {
    return (
      <div className="flex min-w-0 flex-1 items-center justify-end gap-3 text-[0.85rem] text-sky-ink">
        <span
          aria-hidden
          className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-sky/40 border-t-sky-ink"
        />
        <span className="min-w-0 truncate">{compare.message}</span>
        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 font-medium underline decoration-sky-ink/30 underline-offset-2 hover:decoration-sky-ink"
        >
          Cancel
        </button>
      </div>
    );
  }

  if (compare.status === "done") {
    return (
      <div className="flex min-w-0 flex-1 items-center justify-end gap-3 text-[0.82rem] text-ink-muted">
        <span className="min-w-0 truncate">
          <span className="text-sage-ink" aria-hidden>
            ✓
          </span>{" "}
          vs <span className="font-medium text-ink-soft">{compare.fileName}</span>
          {compare.skippedCount > 0 && <> · kept {compare.skippedCount} you wrote</>}
        </span>
        {pick}
      </div>
    );
  }

  if (compare.status === "error") {
    return (
      <div className="flex min-w-0 flex-1 items-center justify-end gap-3 text-[0.82rem] text-ink">
        <span className="min-w-0 truncate">
          <span aria-hidden>⚠️</span> {compare.message}
        </span>
        {pick}
      </div>
    );
  }

  return pick;
}
